"use client"

import { useEffect, useState } from "react"
import { getAdViewCount, setAdViewCount, isAdTestModeOn } from "@/lib/ad-cooldown"
import type { AdTier } from "@/lib/ad-tier"

// Moved out of components/booru-detail-modal.tsx (same state, same effect, same dependencies).

// Shared shape for a resolved letterbox-zone fetch — used for both the
// bottom bar (the original, tier 3) and the top bar (tier 2, a genuinely
// distinct zone — see the comment on letterboxTopAdData below).
export type LetterboxAdState = {
  bannerImageUrl?: string | null
  bannerClickUrl?: string | null
  videoAdUrl?: string | null
  embedHtml?: string | null
  // ad_zones UUID — the serve response always carried this, but it was
  // dropped here, leaving the letterbox placement untrackable.
  zoneId?: string | null
  // Real creative_width/height for the zone the API actually served.
  // Multiple letterbox zones exist (300x250 and 900x250) sharing this one
  // placement — without these, LetterboxAd always assumed its hardcoded
  // 900x250 default, so whenever the 300x250 zone won the fetch, its real
  // (smaller/differently-shaped) creative got rendered inside a frame sized
  // for the wrong zone.
  creativeWidth?: number | null
  creativeHeight?: number | null
}

export function useGutterAds({
  open,
  isAlbum,
  effectivePremiumUser,
  postId,
  modalAdTier,
  onReset,
}: {
  open: boolean
  isAlbum: boolean
  effectivePremiumUser: boolean
  postId: string | number | undefined
  modalAdTier: AdTier
  /** Called when the bars are cleared (modal closed, album, premium) so the modal can forget the media's aspect ratio too. */
  onReset: () => void
}) {
  const [letterboxAdDismissed, setLetterboxAdDismissed] = useState(false)
  const [letterboxAdData, setLetterboxAdData] = useState<LetterboxAdState | null>(null)
  // Top bar — a SECOND, genuinely distinct zone (tier 2, same 900x250 shape),
  // not the same zone rendered twice. Letterboxing a 16:9 video in a portrait
  // frame always splits the dead space evenly top and bottom, so both bars
  // exist whenever one does — but rendering the identical zone ID into both
  // slots simultaneously double-counts impressions and puts one zone on the
  // page twice, which ad networks treat as invalid inventory (same reasoning
  // PillarboxAd's right-only gutter already documents). Three distinct
  // letterbox zones exist in ad_zones, one per tier — tier 3 was already
  // wired to the bottom bar; tier 2 was sitting unused and is a real,
  // separate zone, so it's the top bar's own.
  const [letterboxTopAdData, setLetterboxTopAdData] = useState<LetterboxAdState | null>(null)
  const [pillarboxAdDismissed, setPillarboxAdDismissed] = useState(false)
  const [pillarboxAdData, setPillarboxAdData] = useState<{
    bannerImageUrl?: string | null
    bannerClickUrl?: string | null
    embedHtml?: string | null
    zoneId?: string | null
    // Same reasoning as letterboxAdData above — 3 pillarbox zones exist
    // (300x250, 900x250, 160x600) sharing this placement, but PillarboxAd's
    // hardcoded default (160x600) only matches one of them. Serving the
    // 300x250 or 900x250 zone into a frame sized for 160x600 is the "side
    // ads have scrollbars" report — a landscape creative crammed into a
    // narrow vertical-skyscraper-sized iframe overflows internally.
    creativeWidth?: number | null
    creativeHeight?: number | null
  } | null>(null)
  // Left gutter — a genuinely distinct zone (tier 1, 300x250), not the same
  // tier-3 zone rendered twice. Same reasoning as letterboxTopAdData: this
  // tier-1 pillarbox row was sitting completely unused (nothing else on the
  // site fetches placement=pillarbox&tier=1), so it's real, idle inventory
  // rather than a duplicate.
  const [pillarboxLeftAdData, setPillarboxLeftAdData] = useState<{
    bannerImageUrl?: string | null
    bannerClickUrl?: string | null
    embedHtml?: string | null
    zoneId?: string | null
    creativeWidth?: number | null
    creativeHeight?: number | null
  } | null>(null)

  // Letterbox/pillarbox ad: fetch bar-ad data when a post opens. Gated on
  // the admin config's letterbox_enabled/letterbox_frequency, which existed
  // with a working admin UI (VideoAdsManager) but were never actually read
  // anywhere — this fetch previously ran unconditionally on every video, and
  // only ever fired for video (H7: now covers images too — the geometry
  // that creates these bars/gutters is identical for a portrait image as
  // for a portrait video; scoped to !isAlbum to avoid the added complexity
  // of tracking per-page media dimensions through album pagination).
  useEffect(() => {
    if (!open || isAlbum || effectivePremiumUser) {
      onReset()
      setLetterboxAdDismissed(false)
      setLetterboxAdData(null)
      setLetterboxTopAdData(null)
      setPillarboxAdDismissed(false)
      setPillarboxAdData(null)
      setPillarboxLeftAdData(null)
      return
    }

    fetch("/api/admin/video-ads-config")
      .then((r) => r.ok ? r.json() : null)
      .then((cfg) => {
        const config = cfg?.config ?? {}
        if (config.letterbox_enabled === false) return

        const frequency = config.letterbox_frequency > 0 ? config.letterbox_frequency : 1
        const count = getAdViewCount("letterbox") + 1
        if (!isAdTestModeOn() && count < frequency) {
          setAdViewCount("letterbox", count)
          return
        }
        setAdViewCount("letterbox", 0)

        // Dedicated letterbox zones (900x250, "[Thin]" naming) — previously
        // requested type=display, which resolves to the grid placement and
        // served the 300x250 grid zone into this bar (wrong shape, scaled
        // to 48%). H9: real 900x250 zones now exist per-tier for this
        // placement specifically.
        fetch(`/api/ads/network-grid-ad?type=letterbox&tier=${modalAdTier}`)
          .then((r) => r.ok ? r.json() : null)
          .then((d) => {
            if (d) {
              // The route only fills display_html when zone_type is
              // html_embed; every configured zone here is js_embed, which
              // comes back as display_js instead — reading display_html
              // alone meant this was always null and LetterboxAd never had
              // anything to render.
              setLetterboxAdData({
                bannerImageUrl: d.banner_image_url ?? null,
                bannerClickUrl: d.banner_click_url ?? null,
                videoAdUrl: d.video_url ?? null,
                embedHtml: d.display_js ?? d.display_html ?? null,
                zoneId: d.zone_id ?? null,
                creativeWidth: d.creative_width ?? null,
                creativeHeight: d.creative_height ?? null,
              })
            }
          })
          .catch(() => {})

        // The paired slots (top bar tier 2, left gutter tier 1) only exist to put a DIFFERENT zone next to the
        // tier-3 one. On an SFW-only source the main slots already ask tier 1, so the left gutter would repeat the
        // same zone (invalid inventory) and the top bar would put tier 2 on an SFW page. Explicit pages only.
        const pairedSlots = modalAdTier === 3

        // Top bar — tier 2's zone specifically, a real, separate zone from
        // the tier-3 one just above (see letterboxTopAdData's own comment).
        if (pairedSlots) fetch(`/api/ads/network-grid-ad?type=letterbox&tier=2`)
          .then((r) => r.ok ? r.json() : null)
          .then((d) => {
            if (d) {
              setLetterboxTopAdData({
                bannerImageUrl: d.banner_image_url ?? null,
                bannerClickUrl: d.banner_click_url ?? null,
                videoAdUrl: d.video_url ?? null,
                embedHtml: d.display_js ?? d.display_html ?? null,
                zoneId: d.zone_id ?? null,
                creativeWidth: d.creative_width ?? null,
                creativeHeight: d.creative_height ?? null,
              })
            }
          })
          .catch(() => {})

        // H8: desktop side-gutter zones (160x600 Vertical Skyscraper).
        // Mutually exclusive with the letterbox bar in practice — a given
        // media's aspect ratio can only be "wider than its frame" (bars) or
        // "narrower than its frame" (gutters), never both — but both are
        // fetched here since either component may end up rendering
        // depending on the actual container geometry once measured.
        fetch(`/api/ads/network-grid-ad?type=pillarbox&tier=${modalAdTier}`)
          .then((r) => r.ok ? r.json() : null)
          .then((d) => {
            if (d) {
              setPillarboxAdData({
                bannerImageUrl: d.banner_image_url ?? null,
                bannerClickUrl: d.banner_click_url ?? null,
                embedHtml: d.display_js ?? d.display_html ?? null,
                zoneId: d.zone_id ?? null,
                creativeWidth: d.creative_width ?? null,
                creativeHeight: d.creative_height ?? null,
              })
            }
          })
          .catch(() => {})

        // Left gutter — tier 1's zone specifically, a real, separate zone
        // from the tier-3 one just above (see pillarboxLeftAdData's comment).
        if (pairedSlots) fetch(`/api/ads/network-grid-ad?type=pillarbox&tier=1`)
          .then((r) => r.ok ? r.json() : null)
          .then((d) => {
            if (d) {
              setPillarboxLeftAdData({
                bannerImageUrl: d.banner_image_url ?? null,
                bannerClickUrl: d.banner_click_url ?? null,
                embedHtml: d.display_js ?? d.display_html ?? null,
                zoneId: d.zone_id ?? null,
                creativeWidth: d.creative_width ?? null,
                creativeHeight: d.creative_height ?? null,
              })
            }
          })
          .catch(() => {})
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, postId, isAlbum])

  return {
    letterboxAdDismissed, setLetterboxAdDismissed, letterboxAdData, letterboxTopAdData,
    pillarboxAdDismissed, setPillarboxAdDismissed, pillarboxAdData, pillarboxLeftAdData,
  }
}
